import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { ApiError, errorMessage } from '../lib/api';
import { FullLogo } from '../components/Layout';
import { ErrorAlert, Spinner } from '../components/ui';
import { LanguageSwitcher, useT } from '../i18n';

export function LoginPage() {
  const { config, session, loading, loginLocal, loginOidc } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (session) navigate('/', { replace: true });
  }, [session, navigate]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await loginLocal(email, password);
    } catch (err) {
      setError(err instanceof ApiError && err.status === 401 ? t('login.badCredentials') : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-brand-row">
            <FullLogo />
            <LanguageSwitcher />
          </div>
          <div className="login-brand-title">{t('login.subtitle')}</div>
        </div>
        <ErrorAlert error={error} />
        {loading ? (
          <Spinner />
        ) : (
          <>
            {config?.oidc && (
              <button
                className="btn btn-primary"
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => loginOidc().catch((err) => setError(errorMessage(err)))}
              >
                {t('login.corporate')}
              </button>
            )}
            {config?.oidc && config.local && <div className="divider">{t('login.orLocal')}</div>}
            {config?.local && (
              <form onSubmit={submit} className="stack">
                <label className="field">
                  {t('login.email')}
                  <input type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
                </label>
                <label className="field">
                  {t('login.password')}
                  <input type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
                </label>
                <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled={busy}>
                  {busy && <Spinner />} {t('login.submit')}
                </button>
              </form>
            )}
            {!config?.oidc && !config?.local && <div className="alert error">{t('login.noMethod')}</div>}
          </>
        )}
        <p className="muted" style={{ fontSize: 12.5, marginTop: 22, marginBottom: 0 }}>
          {t('login.customerQuestion')} <Link to="/portal/activacion-offline">{t('login.portalLink')}</Link>
        </p>
      </div>
    </div>
  );
}

export function AuthCallbackPage() {
  const { completeOidc, config } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!config) return;
    completeOidc()
      .then(() => navigate('/', { replace: true }))
      .catch((err) => setError(errorMessage(err)));
  }, [config, completeOidc, navigate]);
  return (
    <div className="login-page">
      <div className="login-card">
        {error ? (
          <>
            <ErrorAlert error={error} />
            <Link to="/login">{t('login.back')}</Link>
          </>
        ) : (
          <p>
            <Spinner /> {t('login.completing')}
          </p>
        )}
      </div>
    </div>
  );
}
